/**
 * Tool Runner component for Arrange phase in registry-demo.
 * 
 * Receives items from the parent (typically from a DEGAP cycle)
 * and runs the selected tool against them.
 */

import { useState, useEffect, useMemo } from "react";
import { useArrangeStore } from '../../stores/prioritization/arrangeStore';
import { useSessionApi } from '../../platform/context';
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { useCycleProgress } from '../../stores/prioritization/cycleProgressStore';
import { getAdapterByName } from "../../harmonizer";
import type { ToolConfig } from "../../harmonizer/types";
import type { Item } from '../../types/prioritization/lifecycle';
import { AlertTriangle, Lock } from 'lucide-react';
import { UI_LABELS } from '../../lib/terminology';

interface ToolRunnerProps {
  /** Items to run the tool against (from DEGAP cycle) */
  items?: Item[];
  /** Cycle ID for pool validation */
  cycleId?: string;
}

export function ToolRunner({ items = [], cycleId }: ToolRunnerProps) {
  const { selectedTool, toolConfig, setToolConfig, setLastToolResult, addSnapshot } = useArrangeStore();
  const sessionContext = useSessionApi();
  const sessionApi = sessionContext?.sessionApi;
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Invariant-based: tool run allowed when structural gates are satisfied (no phase gating)
  const itemPools = useItemPoolsStore(state => state.itemPools);
  const isItemPoolLocked = useItemPoolsStore(state => state.isItemPoolLocked);
  const { progress: cycleProgress } = useCycleProgress(cycleId);
  const canRunTools = cycleId
    ? (cycleProgress?.canEnterArrange && (cycleProgress?.gateAPoolLocked ?? false))
    : true;
  
  // Find the item pool for this cycle
  const cycleItemPool = useMemo(() => {
    return itemPools.find(fl => (fl as any).cycleId === cycleId);
  }, [itemPools, cycleId]);
  
  const poolItemIds = cycleItemPool?.priorityIds || [];
  const isPoolLocked = cycleItemPool ? isItemPoolLocked(cycleItemPool.id) : false;
  
  // Validate that all items are in the locked pool
  const poolValidation = useMemo(() => {
    if (!cycleId) {
      return { isValid: true, message: '' };
    }
    
    if (!cycleItemPool) {
      return { isValid: false, message: `No ${UI_LABELS.itemPool.toLowerCase()} selected. Select items in the Arrange phase first.` };
    }

    if (!isPoolLocked) {
      return { isValid: false, message: `${UI_LABELS.itemPool} must be locked before ranking. Lock the pool in the Arrange phase.` };
    }
    
    // Check that all items being ranked are in the pool
    const itemsNotInPool = items.filter(item => !poolItemIds.includes(item.id));
    if (itemsNotInPool.length > 0) {
      return {
        isValid: false,
        message: `${itemsNotInPool.length} item(s) are not in the locked ${UI_LABELS.itemPool.toLowerCase()}. Only pooled items can be ranked.`
      };
    }
    
    return { isValid: true, message: '', itemPoolId: cycleItemPool.id };
  }, [cycleId, cycleItemPool, isPoolLocked, items, poolItemIds]);
  
  // Update session items when items prop changes
  useEffect(() => {
    if (!sessionApi) return;
    
    if (items.length > 0) {
      // Ensure we have an active session with these items
      let activeSessionId = sessionApi.getActiveSessionId();
      if (!activeSessionId) {
        activeSessionId = sessionApi.createAndSetActiveSession(items);
      } else {
        // Update the existing session with new items
        sessionApi.updateItems(items);
      }
    }
  }, [items, sessionApi]);

  if (!selectedTool) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-sm text-gray-600">
          Please select a tool first.
        </p>
      </div>
    );
  }

  const adapter = getAdapterByName(selectedTool);
  if (!adapter) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">
          Tool "{selectedTool}" not found in registry.
        </p>
      </div>
    );
  }

  const handleRunTool = async () => {
    if (!sessionApi) {
      setError("Session API not available. Please refresh the page.");
      return;
    }
    
    if (!canRunTools) {
      setError(
        cycleProgress
          ? "Criteria and pool must be locked and pool selected before running tools."
          : "Cycle progress unavailable; cannot validate tool readiness."
      );
      return;
    }
    
    if (items.length === 0) {
      setError("No items to prioritize. Add items from the Gather phase first.");
      return;
    }
    
    // Per Doc 09 A9: Item Pool is the only allowed input to ranking tools
    if (cycleId && !poolValidation.isValid) {
      setError(poolValidation.message);
      return;
    }

    setIsRunning(true);
    setError(null);

    try {
      // Ensure session has the current items
      let activeSessionId = sessionApi.getActiveSessionId();
      if (!activeSessionId) {
        activeSessionId = sessionApi.createAndSetActiveSession(items);
      } else {
        // Make sure session items are up to date
        await sessionApi.updateItems(items);
      }

      const snapshot = await sessionApi.runToolOnActive(selectedTool, {
        toolConfig,
      });

      // Add itemPoolId to snapshot for provenance per Doc 04
      const snapshotWithPool = {
        ...snapshot,
        itemPoolId: (poolValidation as any).itemPoolId || cycleItemPool?.id,
        cycleId,
      };

      setLastToolResult({
        snapshot: snapshotWithPool,
        timestamp: Date.now(),
      });
      addSnapshot(snapshotWithPool);

      console.log("Tool run completed:", snapshotWithPool);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(`Tool execution failed: ${message}`);
      console.error("Tool execution error:", err);
    } finally {
      setIsRunning(false);
    }
  };

  const canRun = canRunTools && items.length > 0 && (!cycleId || poolValidation.isValid);

  return (
    <div className="space-y-4">
      {/* Pool validation warning - Per Doc 09 A9 */}
      {cycleId && !poolValidation.isValid && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">{UI_LABELS.itemPool} Required</p>
              <p className="text-sm text-amber-700 mt-1">{poolValidation.message}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Pool locked indicator */}
      {cycleId && isPoolLocked && (
        <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-green-600" />
            <span className="text-sm text-green-700">
              {UI_LABELS.itemPool} locked with {poolItemIds.length} items
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Run {selectedTool}</h3>
          <p className="text-sm text-gray-500">
            {items.length === 0 
              ? 'No items selected' 
              : `${items.length} item${items.length === 1 ? '' : 's'} to evaluate`
            }
          </p>
        </div>
        <button
          onClick={handleRunTool}
          disabled={isRunning || !canRun}
          className={`
            px-4 py-2 rounded-lg font-medium transition-colors
            ${isRunning || !canRun
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
            }
          `}
        >
          {isRunning ? "Running..." : "Run Tool"}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {adapter.inputContract.toolConfigFields.length > 0 && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600">
            Tool configuration: {adapter.inputContract.toolConfigFields.length} fields
            {/* TODO: Render dynamic form based on toolConfigFields */}
          </p>
        </div>
      )}
    </div>
  );
}


